import React, { useState, useEffect, Suspense } from 'react';
import { ThirdwebProvider } from "thirdweb/react";
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { client } from './client';

import Background from './components/Background';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import InactivityManager from './components/InactivityManager';
import ErrorBoundary from './components/ErrorBoundary';
import InstallPrompt from './components/InstallPrompt';

// Lazy-loaded pages
const LeaderboardPage = React.lazy(() => import('./components/LeaderboardPage'));
const RoadmapPage = React.lazy(() => import('./components/RoadmapPage'));
const EkoCollectionPage = React.lazy(() => import('./components/EkoCollectionPage'));
const ActivityPage = React.lazy(() => import('./components/ActivityPage'));
const PrivacyPolicyPage = React.lazy(() => import('./components/PrivacyPolicyPage'));
const TermsPage = React.lazy(() => import('./components/TermsPage'));
const AscensionRiftPage = React.lazy(() => import('./components/AscensionRiftPage'));
const WhitepaperPage = React.lazy(() => import('./components/WhitepaperPage'));
const CodeShiftPage = React.lazy(() => import('./components/CodeShiftPage'));
const ChroniclesPage = React.lazy(() => import('./components/ChroniclesPage'));
const WhatIsScavenjer = React.lazy(() => import('./components/WhatIsScavenjer'));
const ScavenjerStudiosPage = React.lazy(() => import('./components/ScavenjerStudiosPage'));
const ARDropsPage = React.lazy(() => import('./components/ARDropsPage'));

const NotFoundPage = React.lazy(() => import('./components/NotFoundPage'));
const IntroSection = React.lazy(() => import('./components/IntroSection'));
const ScavenjerTypesSection = React.lazy(() => import('./components/scavenjer-types/ScavenjerTypesSection'));
const HostsSection = React.lazy(() => import('./components/hosts/HostsSection'));
const PartnershipSection = React.lazy(() => import('./components/PartnershipSection'));
const InfoGrid = React.lazy(() => import('./components/InfoGrid'));
const SocialMediaSection = React.lazy(() => import('./components/SocialMediaSection'));
const HostsPage = React.lazy(() => import('./components/HostsPage'));
const HostProfilePage = React.lazy(() => import('./components/HostProfilePage'));
const HostApplicationPage = React.lazy(() => import('./components/hosts/HostApplicationPage'));
const TheBroadcastPage = React.lazy(() => import('./components/TheBroadcastPage'));
const PartnershipsPage = React.lazy(() => import('./components/PartnershipsPage'));
const CareersPage = React.lazy(() => import('./components/CareersPage'));
const ConnectPage = React.lazy(() => import('./components/ConnectPage'));
const ConnectMarbleverse = React.lazy(() => import('./pages/ConnectMarbleverse'));
const ProfilePage = React.lazy(() => import('./components/ProfilePage'));
const AdminPortal = React.lazy(() => import('./pages/admin/AdminPortal'));
const StorefrontPage = React.lazy(() => import('./components/StorefrontPage'));
const MintStatusPage = React.lazy(() => import('./components/MintStatusPage'));

// External redirect component for redirecting to external URLs
function ExternalRedirect({ url }: { url: string }) {
  useEffect(() => {
    window.location.href = url;
  }, [url]);
  
  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2DD4BF] mx-auto mb-4"></div>
        <p>Redirecting...</p>
      </div>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function ScavenjerHome() {
  return (
    <>
      <IntroSection />
      <WhatIsScavenjer />
      <ScavenjerTypesSection />
      <HostsSection />
      <PartnershipSection />
      <InfoGrid />
      <SocialMediaSection />
    </>
  );
}

function AppContent() {
  const [currentPage, setCurrentPage] = useState('home');
  const location = useLocation();
  const hideGlobalChrome = location.pathname === '/business';

  return (
    <>
      <InactivityManager />
      <ScrollToTop />
      <Analytics />
      <SpeedInsights />
      <div className="min-h-screen bg-[#111111] text-white relative">
        <Background />
        <Suspense fallback={<LoadingScreen />}>
          {!hideGlobalChrome && (
            <Header currentPage={currentPage} onPageChange={setCurrentPage} client={client} />
          )}
          <main className="relative z-10">
            <Routes>
                <Route path="/" element={<ScavenjerHome />} />
                <Route path="/entertainment" element={<Navigate to="/" replace />} />
                {/* Drop System Route */}
                <Route path="/drops" element={<ARDropsPage />} />
                
                {/* Legacy campaign redirects */}
                <Route path="/token" element={<Navigate to="/drops" replace />} />
                <Route path="/token/minting" element={<Navigate to="/drops" replace />} />
                <Route path="/stages" element={<Navigate to="/drops" replace />} />
                <Route path="/leaderboard" element={<LeaderboardPage />} />
                <Route path="/collection" element={<EkoCollectionPage />} />
                <Route path="/activity" element={<ActivityPage />} />
                <Route path="/ascension" element={<AscensionRiftPage />} />
                <Route path="/neomarket" element={<ExternalRedirect url="https://neomarket.scavenjer.com/explore" />} />
                <Route path="/roadmap" element={<RoadmapPage />} />
                <Route path="/broadcast" element={<TheBroadcastPage />} />
                <Route path="/studios" element={<ScavenjerStudiosPage />} />
                <Route path="/universe" element={<ExternalRedirect url="https://simulations-gamma.vercel.app/" />} />
                <Route path="/game" element={<ExternalRedirect url="https://simulations-gamma.vercel.app/" />} />
                <Route path="/whitepaper" element={<WhitepaperPage />} />
                <Route path="/chronicles" element={<ChroniclesPage />} />
                <Route path="/codeshift" element={<CodeShiftPage />} />
                <Route path="/about" element={<Navigate to="/" replace />} />
                <Route path="/marketplace" element={<Navigate to="/collection" replace />} />
                <Route path="/whitelist" element={<Navigate to="/drops" replace />} />
                <Route path="/dark-circuit" element={<ExternalRedirect url="https://neomarket.scavenjer.com/explore" />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/partnerships" element={<PartnershipsPage />} />
                <Route path="/business" element={<ExternalRedirect url="https://scavenjerbusiness.com/" />} />
                <Route path="/hosts" element={<HostsPage />} />
                <Route path="/host-profile" element={<HostProfilePage />} />
                <Route path="/host-application" element={<HostApplicationPage />} />
                <Route path="/careers" element={<CareersPage />} />
                <Route path="/connect" element={<ConnectPage />} />
                <Route path="/connect-marbleverse" element={<ConnectMarbleverse />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/admin" element={<AdminPortal />} />
                
                {/* Support Shop Routes */}
                <Route path="/support-shop" element={<StorefrontPage />} />
                <Route path="/support-shop/success" element={<MintStatusPage />} />
                {/* Redirect old store URLs */}
                <Route path="/store" element={<Navigate to="/support-shop" replace />} />
                <Route path="/store/success" element={<Navigate to="/support-shop/success" replace />} />

                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            {!hideGlobalChrome && <Footer onPageChange={setCurrentPage} />}
            {!hideGlobalChrome && <InstallPrompt />}
          </Suspense>
        </div>
      </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThirdwebProvider>
        <Router>
          <AppContent />
      </Router>
    </ThirdwebProvider>
    </ErrorBoundary>
  );
}

export default App;
